import { useId, useState } from "react";
import type { KeyboardEvent } from "react";
import { makeDefaultExcelChartSettings } from "../../logic/excelLayoutDefaults";
import { comboHasColumnSeries, comboHasLineSeries, isComboChart } from "../../logic/chartCombo";
import type {
    ExcelChartDataOrientation,
    ExcelChartLayout,
    ExcelChartLegendPosition,
    ExcelChartLineWidth,
    ExcelChartPalette,
    ExcelChartSettings,
    ExcelChartValueFormat,
} from "../../types";
import { ReportConfigSelect } from "../ReportConfigSelect";

interface ChartSettingsEditorProps {
    chart: ExcelChartLayout;
    fallbackTitle: string;
    defaultDataOrientation: ExcelChartDataOrientation;
    disabled?: boolean;
    onChange: (chart: ExcelChartLayout) => void;
}

const LEGEND_POSITION_OPTIONS: { value: ExcelChartLegendPosition; label: string }[] = [
    { value: "bottom", label: "Bottom" },
    { value: "top", label: "Top" },
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
];

const VALUE_FORMAT_OPTIONS: { value: ExcelChartValueFormat; label: string }[] = [
    { value: "general", label: "Use report format" },
    { value: "accountingInt", label: "Accounting (whole number)" },
    { value: "integer", label: "Whole number" },
    { value: "percent0", label: "Percent (0 decimals)" },
    { value: "percent2", label: "Percent (2 decimals)" },
    { value: "decimal2", label: "Number (2 decimals)" },
];

const PALETTE_OPTIONS: { value: ExcelChartPalette; label: string }[] = [
    { value: "office", label: "Excel default" },
    { value: "blue", label: "Professional blue" },
    { value: "colorful", label: "Colorful" },
    { value: "warm", label: "Warm" },
    { value: "cool", label: "Cool" },
    { value: "monochrome", label: "Black and gray" },
];

const LINE_WIDTH_OPTIONS: { value: ExcelChartLineWidth; label: string }[] = [
    { value: "automatic", label: "Automatic" },
    { value: "thin", label: "Thin" },
    { value: "standard", label: "Standard" },
    { value: "thick", label: "Thick" },
];

function optionalNumber(value: string) {
    if (!value.trim()) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function optionalInteger(value: string) {
    const parsed = optionalNumber(value);
    return parsed == null ? null : Math.trunc(parsed);
}

type ChartSettingsTab = "elements" | "axes" | "style" | "data" | "size";

// Grouped the way Excel groups chart formatting (Chart Elements, Axes, Style, Data, Size),
// so each option has one obvious home instead of one long form.
const CHART_SETTINGS_TABS: { key: ChartSettingsTab; label: string; icon: string; hint: string }[] = [
    { key: "elements", label: "Chart Elements", icon: "title", hint: "Title, legend, data labels, and gridlines." },
    { key: "axes", label: "Axes", icon: "straighten", hint: "Axis labels, number format, and the Y-axis value range." },
    { key: "style", label: "Style", icon: "palette", hint: "Colors, lines, markers, and column spacing." },
    { key: "data", label: "Data", icon: "table_chart", hint: "What runs along the bottom axis and which rows are plotted." },
    { key: "size", label: "Size", icon: "aspect_ratio", hint: "Exact chart width and height in the worksheet." },
];

// Which settings each tab owns - drives the "changed" dot on the tab.
const TAB_SETTING_KEYS: Record<ChartSettingsTab, (keyof ExcelChartSettings)[]> = {
    elements: ["showTitle", "title", "showLegend", "legendPosition", "showDataLabels", "showMajorGridlines"],
    axes: ["categoryAxisTitle", "valueAxisTitle", "valueNumberFormat", "valueAxisMinimum", "valueAxisMaximum", "valueAxisInterval"],
    style: ["palette", "lineWidth", "showLineMarkers", "gapWidth"],
    data: [],
    size: ["widthColumns", "heightRows"],
};

const sameSetting = (left: unknown, right: unknown) => (left ?? null) === (right ?? null);

export function ChartSettingsEditor({ chart, fallbackTitle, defaultDataOrientation, disabled = false, onChange }: ChartSettingsEditorProps) {
    const defaults = makeDefaultExcelChartSettings();
    const settings: ExcelChartSettings = { ...defaults, ...(chart.settings || {}) };
    const seriesRowLabels = Array.isArray(chart.seriesRowLabels) ? chart.seriesRowLabels : [];
    const isCombo = isComboChart(chart);
    // In combo mode the line/column options apply to whichever series use that type.
    const isLine = isCombo ? comboHasLineSeries(chart) : chart.type === "line";
    const showGapWidth = isCombo ? comboHasColumnSeries(chart) : chart.type !== "line";
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState<ChartSettingsTab>("elements");
    const [isCustomTitleEnabled, setIsCustomTitleEnabled] = useState(() => settings.title.trim().length > 0);
    const settingsPanelId = useId();
    const expectedSettings = chart.dataOrientation === "rowsAsCategories" && defaultDataOrientation === "rowsAsCategories"
        ? { ...defaults, showLegend: false }
        : defaults;
    const dataOrientation = chart.dataOrientation || "rowsAsSeries";
    const dataOrientationOptions: { value: ExcelChartDataOrientation; label: string }[] = [
        { value: "rowsAsCategories", label: "Row labels" },
        { value: "rowsAsSeries", label: "Table columns" },
    ];

    const tabChanged = (tab: ChartSettingsTab) => {
        if (tab === "data") return seriesRowLabels.length > 0 || (chart.dataOrientation != null && chart.dataOrientation !== defaultDataOrientation);
        return TAB_SETTING_KEYS[tab].some((key) => !sameSetting(settings[key], expectedSettings[key]));
    };
    const hasCustomSettings = CHART_SETTINGS_TABS.some((tab) => tabChanged(tab.key));

    // Same rules the Excel Layout validation enforces on save, surfaced on the owning tab.
    const intervalInvalid = settings.valueAxisInterval != null && !(settings.valueAxisInterval > 0);
    const rangeInvalid = settings.valueAxisMinimum != null && settings.valueAxisMaximum != null && settings.valueAxisMinimum >= settings.valueAxisMaximum;
    const widthInvalid = settings.widthColumns != null && (settings.widthColumns < 6 || settings.widthColumns > 30);
    const heightInvalid = settings.heightRows != null && (settings.heightRows < 8 || settings.heightRows > 30);
    const tabHasIssue = (tab: ChartSettingsTab) => (tab === "axes" && (intervalInvalid || rangeInvalid)) || (tab === "size" && (widthInvalid || heightInvalid));

    function updateSettings(patch: Partial<ExcelChartSettings>) {
        onChange({ ...chart, settings: { ...settings, ...patch } });
    }

    function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
        const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
        const target = event.key === "Home" ? 0 : event.key === "End" ? CHART_SETTINGS_TABS.length - 1 : step ? (index + step + CHART_SETTINGS_TABS.length) % CHART_SETTINGS_TABS.length : -1;
        if (target < 0) return;
        event.preventDefault();
        const next = CHART_SETTINGS_TABS[target].key;
        setActiveTab(next);
        document.getElementById(`${settingsPanelId}-tab-${next}`)?.focus();
    }

    const activeTabDef = CHART_SETTINGS_TABS.find((tab) => tab.key === activeTab) || CHART_SETTINGS_TABS[0];

    function renderElementsTab() {
        return (
            <>
                <section className="rc-chart-settings-group">
                    <div className="rc-chart-settings-heading rc-chart-title-heading">
                        <span>
                            <b>Chart title</b>
                            <small>Use the report title automatically, or enter a shorter title for this chart.</small>
                        </span>
                        <div className="rc-chart-checkboxes rc-chart-title-controls">
                            <label className="rc-checkbox-row rc-chart-checkbox">
                                <input type="checkbox" checked={settings.showTitle} disabled={disabled} onChange={(event) => updateSettings({ showTitle: event.target.checked })} />
                                <span>Show title</span>
                            </label>
                            <label className="rc-checkbox-row rc-chart-checkbox">
                                <input
                                    type="checkbox"
                                    checked={isCustomTitleEnabled}
                                    disabled={disabled || !settings.showTitle}
                                    onChange={(event) => {
                                        setIsCustomTitleEnabled(event.target.checked);
                                        if (!event.target.checked) updateSettings({ title: "" });
                                    }}
                                />
                                <span>Use custom title</span>
                            </label>
                        </div>
                    </div>
                    {isCustomTitleEnabled && (
                        <label className="rc-label-block rc-chart-title-text-field">
                            Custom title text <small className="rc-chart-technical-label">Chart title</small>
                            <input
                                value={settings.title}
                                maxLength={255}
                                placeholder={fallbackTitle || "Enter a chart title"}
                                disabled={disabled || !settings.showTitle}
                                onChange={(event) => updateSettings({ title: event.target.value })}
                            />
                        </label>
                    )}
                </section>

                <div className="rc-chart-tab-columns">
                    <section className="rc-chart-settings-group">
                        <div className="rc-chart-settings-heading rc-chart-title-heading">
                            <span>
                                <b>Legend</b>
                                <small>Identifies each series by color.</small>
                            </span>
                            <label className="rc-checkbox-row rc-chart-checkbox">
                                <input type="checkbox" checked={settings.showLegend} disabled={disabled} onChange={(event) => updateSettings({ showLegend: event.target.checked })} />
                                <span>Show legend</span>
                            </label>
                        </div>
                        <label className="rc-label-block">
                            Legend position <small className="rc-chart-technical-label">Legend position</small>
                            <ReportConfigSelect<ExcelChartLegendPosition>
                                value={settings.legendPosition}
                                options={LEGEND_POSITION_OPTIONS}
                                ariaLabel="Chart legend position"
                                disabled={disabled || !settings.showLegend}
                                onChange={(legendPosition) => updateSettings({ legendPosition })}
                            />
                        </label>
                    </section>

                    <section className="rc-chart-settings-group">
                        <div className="rc-chart-settings-heading">
                            <b>Labels and gridlines</b>
                            <small>Show values on the chart and guide lines behind it.</small>
                        </div>
                        <div className="rc-chart-checkboxes rc-chart-checkbox-stack">
                            <label className="rc-checkbox-row rc-chart-checkbox">
                                <input type="checkbox" checked={settings.showDataLabels} disabled={disabled} onChange={(event) => updateSettings({ showDataLabels: event.target.checked })} />
                                <span>Show data labels</span>
                            </label>
                            <label className="rc-checkbox-row rc-chart-checkbox">
                                <input type="checkbox" checked={settings.showMajorGridlines} disabled={disabled} onChange={(event) => updateSettings({ showMajorGridlines: event.target.checked })} />
                                <span>Show gridlines</span>
                            </label>
                        </div>
                    </section>
                </div>
            </>
        );
    }

    function renderAxesTab() {
        return (
            <div className="rc-chart-tab-columns">
                <section className="rc-chart-settings-group">
                    <div className="rc-chart-settings-heading">
                        <b>Horizontal axis</b>
                        <small>The category axis along the bottom of the chart.</small>
                    </div>
                    <label className="rc-label-block">
                        Bottom label <small className="rc-chart-technical-label">X-axis / category axis title</small>
                        <input value={settings.categoryAxisTitle} maxLength={255} placeholder="Example: Month" disabled={disabled} onChange={(event) => updateSettings({ categoryAxisTitle: event.target.value })} />
                    </label>
                </section>

                <section className="rc-chart-settings-group">
                    <div className="rc-chart-settings-heading">
                        <b>{isCombo ? "Primary vertical axis" : "Vertical axis"}</b>
                        <small>{isCombo ? "The left value axis. The secondary axis is set in the combo series settings." : "The value axis along the side of the chart."}</small>
                    </div>
                    <div className="rc-chart-advanced-grid">
                        <label className="rc-label-block">
                            {isCombo ? "Left axis label" : "Side label"} <small className="rc-chart-technical-label">{isCombo ? "Primary Y-axis title" : "Y-axis / value axis title"}</small>
                            <input value={settings.valueAxisTitle} maxLength={255} placeholder="Example: Number of trips" disabled={disabled} onChange={(event) => updateSettings({ valueAxisTitle: event.target.value })} />
                        </label>
                        <label className="rc-label-block">
                            Value format <small className="rc-chart-technical-label">Y-axis number format</small>
                            <ReportConfigSelect<ExcelChartValueFormat>
                                value={settings.valueNumberFormat}
                                options={VALUE_FORMAT_OPTIONS}
                                ariaLabel="Chart value number format"
                                disabled={disabled}
                                onChange={(valueNumberFormat) => updateSettings({ valueNumberFormat })}
                            />
                        </label>
                    </div>
                    <div className="rc-chart-subheading">
                        <b>Bounds and units</b>
                        <small>Leave on Automatic to let Excel pick the range.</small>
                    </div>
                    <div className="rc-chart-three-column-grid">
                        <label className="rc-label-block">
                            Start value <small className="rc-chart-technical-label">Y-axis minimum</small>
                            <input type="number" value={settings.valueAxisMinimum ?? ""} placeholder="Automatic" disabled={disabled} aria-invalid={rangeInvalid} onChange={(event) => updateSettings({ valueAxisMinimum: optionalNumber(event.target.value) })} />
                        </label>
                        <label className="rc-label-block">
                            End value <small className="rc-chart-technical-label">Y-axis maximum</small>
                            <input type="number" value={settings.valueAxisMaximum ?? ""} placeholder="Automatic" disabled={disabled} aria-invalid={rangeInvalid} onChange={(event) => updateSettings({ valueAxisMaximum: optionalNumber(event.target.value) })} />
                        </label>
                        <label className="rc-label-block">
                            Interval <small className="rc-chart-technical-label">Y-axis major unit, more than 0</small>
                            <input type="number" min={0} step="any" value={settings.valueAxisInterval ?? ""} placeholder="Automatic" disabled={disabled} aria-invalid={intervalInvalid} onChange={(event) => updateSettings({ valueAxisInterval: optionalNumber(event.target.value) })} />
                        </label>
                    </div>
                    {rangeInvalid && <small className="rc-chart-field-error">Start value must be less than the end value.</small>}
                    {intervalInvalid && <small className="rc-chart-field-error">Interval must be more than 0.</small>}
                </section>
            </div>
        );
    }

    function renderStyleTab() {
        return (
            <div className="rc-chart-tab-columns">
                <section className="rc-chart-settings-group">
                    <div className="rc-chart-settings-heading">
                        <b>Colors</b>
                        <small>The color set used for the chart series.</small>
                    </div>
                    <label className="rc-label-block">
                        Color palette <small className="rc-chart-technical-label">Chart palette</small>
                        <ReportConfigSelect<ExcelChartPalette>
                            value={settings.palette}
                            options={PALETTE_OPTIONS}
                            ariaLabel="Chart color palette"
                            disabled={disabled}
                            onChange={(palette) => updateSettings({ palette })}
                        />
                    </label>
                </section>

                {(isLine || showGapWidth) && (
                    <section className="rc-chart-settings-group">
                        <div className="rc-chart-settings-heading">
                            <b>{isLine && showGapWidth ? "Lines and columns" : isLine ? "Lines" : "Columns"}</b>
                            <small>{isLine && showGapWidth ? "Applies to the line and column series in this chart." : isLine ? "How the line series are drawn." : "How the column series are spaced."}</small>
                        </div>
                        {isLine && (
                            <div className="rc-chart-advanced-grid">
                                <label className="rc-label-block">
                                    Line thickness <small className="rc-chart-technical-label">Line width</small>
                                    <ReportConfigSelect<ExcelChartLineWidth>
                                        value={settings.lineWidth}
                                        options={LINE_WIDTH_OPTIONS}
                                        ariaLabel="Chart line thickness"
                                        disabled={disabled}
                                        onChange={(lineWidth) => updateSettings({ lineWidth })}
                                    />
                                </label>
                                <div className="rc-chart-checkbox-field">
                                    <label className="rc-checkbox-row rc-chart-checkbox">
                                        <input type="checkbox" checked={settings.showLineMarkers} disabled={disabled} onChange={(event) => updateSettings({ showLineMarkers: event.target.checked })} />
                                        <span>Show markers</span>
                                    </label>
                                </div>
                            </div>
                        )}
                        {showGapWidth && (
                            <label className="rc-label-block rc-chart-range-field">
                                Column spacing <small className="rc-chart-technical-label">Gap width</small><span>{settings.gapWidth}</span>
                                <input type="range" min={0} max={500} step={10} value={settings.gapWidth} disabled={disabled} onChange={(event) => updateSettings({ gapWidth: Number(event.target.value) })} />
                                <small className="rc-field-note">Move left for wider columns or right for more space.</small>
                            </label>
                        )}
                    </section>
                )}
            </div>
        );
    }

    function renderDataTab() {
        return (
            <>
                <section className="rc-chart-settings-group">
                    <div className="rc-chart-settings-heading">
                        <b>Data layout</b>
                        <small>Choose what should appear along the bottom axis of the chart.</small>
                    </div>
                    <label className="rc-label-block">
                        Bottom axis categories <small className="rc-chart-technical-label">Data orientation (switch row and column)</small>
                        <ReportConfigSelect<ExcelChartDataOrientation>
                            value={dataOrientation}
                            options={dataOrientationOptions}
                            ariaLabel="Chart data orientation"
                            disabled={disabled || isCombo}
                            onChange={(dataOrientation) => onChange({ ...chart, dataOrientation, settings })}
                        />
                        <small className="rc-field-note">
                            {isCombo
                                ? "Combo charts always use table columns along the bottom axis; each listed row is a series."
                                : dataOrientation === "rowsAsCategories"
                                ? "Each table row becomes a category; table columns become the chart series."
                                : "Each table column becomes a category; table rows become the chart series."}
                        </small>
                    </label>
                </section>

                {!isCombo && (
                    <section className="rc-chart-settings-group">
                        <div className="rc-chart-settings-heading">
                            <b>Data range</b>
                            <small>Use this only when the chart should plot specific rows.</small>
                        </div>
                        <label className="rc-label-block">
                            Rows to include <small className="rc-chart-technical-label">Series filter</small>
                            <input
                                value={seriesRowLabels.join(", ")}
                                placeholder="Leave blank to include all rows"
                                disabled={disabled}
                                onChange={(event) => onChange({ ...chart, settings, seriesRowLabels: event.target.value.split(",").map((value) => value.trim()).filter(Boolean) })}
                            />
                            <small className="rc-field-note">Enter the row names separated by commas.</small>
                        </label>
                    </section>
                )}
            </>
        );
    }

    function renderSizeTab() {
        return (
            <section className="rc-chart-settings-group">
                <div className="rc-chart-settings-heading">
                    <b>Chart size</b>
                    <small>Leave these on Automatic unless a workbook layout needs a fixed size.</small>
                </div>
                <div className="rc-chart-advanced-grid">
                    <label className="rc-label-block">
                        Chart width <small className="rc-chart-technical-label">Columns (6–30)</small>
                        <input type="number" min={6} max={30} step={1} value={settings.widthColumns ?? ""} placeholder="Automatic" disabled={disabled} aria-invalid={widthInvalid} onChange={(event) => updateSettings({ widthColumns: optionalInteger(event.target.value) })} />
                    </label>
                    <label className="rc-label-block">
                        Chart height <small className="rc-chart-technical-label">Rows (8–30)</small>
                        <input type="number" min={8} max={30} step={1} value={settings.heightRows ?? ""} placeholder="Automatic" disabled={disabled} aria-invalid={heightInvalid} onChange={(event) => updateSettings({ heightRows: optionalInteger(event.target.value) })} />
                    </label>
                </div>
                {widthInvalid && <small className="rc-chart-field-error">Chart width must be between 6 and 30 columns.</small>}
                {heightInvalid && <small className="rc-chart-field-error">Chart height must be between 8 and 30 rows.</small>}
            </section>
        );
    }

    return (
        <div className="rc-chart-settings" aria-label="Chart settings">
            <button
                className="rc-chart-settings-toggle"
                type="button"
                aria-expanded={isExpanded}
                aria-controls={settingsPanelId}
                onClick={() => setIsExpanded((current) => !current)}
            >
                <span className="material-icons md-18" aria-hidden="true">tune</span>
                <span className="rc-chart-settings-toggle-copy">
                    <b>Customize this chart</b>
                    <small>Chart elements, axes, style, data, and size.</small>
                </span>
                {hasCustomSettings && <span className="rc-chart-customized-badge">Customized</span>}
                <span className="material-icons md-18 rc-chart-settings-chevron" aria-hidden="true">expand_more</span>
            </button>

            {isExpanded && (
                <div className="rc-chart-settings-body" id={settingsPanelId}>
                    {disabled && (
                        <div className="rc-chart-settings-locked-note">
                            <span className="material-icons md-16" aria-hidden="true">lock</span>
                            This template is locked. Enable template editing to change these options.
                        </div>
                    )}

                    <div className="rc-chart-tabs" role="tablist" aria-label="Chart settings sections">
                        {CHART_SETTINGS_TABS.map((tab, index) => {
                            const selected = tab.key === activeTab;
                            const issue = tabHasIssue(tab.key);
                            const changed = !issue && tabChanged(tab.key);
                            return (
                                <button
                                    key={tab.key}
                                    id={`${settingsPanelId}-tab-${tab.key}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    aria-controls={`${settingsPanelId}-panel`}
                                    tabIndex={selected ? 0 : -1}
                                    className={selected ? "rc-chart-tab active" : "rc-chart-tab"}
                                    onClick={() => setActiveTab(tab.key)}
                                    onKeyDown={(event) => onTabKeyDown(event, index)}
                                >
                                    <span className="material-icons md-16" aria-hidden="true">{tab.icon}</span>
                                    <span>{tab.label}</span>
                                    {selected && <span className="rc-chart-tab-check material-icons md-14" aria-hidden="true">check_circle</span>}
                                    {issue && <span className="rc-chart-tab-flag error" title="Needs attention" aria-label="needs attention">!</span>}
                                    {changed && <span className="rc-chart-tab-flag changed" title="Changed from default" aria-label="changed" />}
                                </button>
                            );
                        })}
                    </div>

                    <div
                        className="rc-chart-tab-panel"
                        id={`${settingsPanelId}-panel`}
                        role="tabpanel"
                        aria-labelledby={`${settingsPanelId}-tab-${activeTab}`}
                    >
                        <div className="rc-chart-tab-panel-legend">
                            <span className="material-icons md-18" aria-hidden="true">{activeTabDef.icon}</span>
                            <span>
                                <b>{activeTabDef.label}</b>
                                <small>{activeTabDef.hint}</small>
                            </span>
                        </div>
                        {activeTab === "elements" && renderElementsTab()}
                        {activeTab === "axes" && renderAxesTab()}
                        {activeTab === "style" && renderStyleTab()}
                        {activeTab === "data" && renderDataTab()}
                        {activeTab === "size" && renderSizeTab()}
                    </div>
                </div>
            )}
        </div>
    );
}
